<template>
  <div class="upload-image-wrapper">
    <div class="container">
      <div class="upload-image-container">
        <div class="card w-100 h-100">
          <h3>Upload</h3>
          <div class="upload-image-content">
            <div class="file-select-zone">
              <svg v-if="showUploadIcon" id="uploadIcon" xmlns="http://www.w3.org/2000/svg" width="80" height="80"
                   fill="lightgray" class="bi bi-file-earmark-arrow-up" viewBox="0 0 16 16">
                <path
                    d="M8.5 11.5a.5.5 0 0 1-1 0V7.707L6.354 8.854a.5.5 0 1 1-.708-.708l2-2a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 7.707z"/>
                <path
                    d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
              </svg>
              <input type="file" class="hidden" ref="fileInput" @change="onFileChange" accept="image/*">
              <img v-if="previewUrl" :src="previewUrl" class="image-preview"/>
            </div>
            <button v-if="!showLoader" class="button-secondary my-20" @click="openFileDialog">
              <span>
                Select image
              </span>
            </button>
            <div v-if="showLoader" class="loader-wrapper">
              <div class="loader"></div>
              <h3>Uploading, please wait.</h3>
            </div>
            <div v-if="!showLoader" class="upload-image-detail">
              <div class="upload-image-detail-half">
                <label for="image_name">Name</label>
                <input class="input" id="image_name" name="image_name" type="text" v-model="newImage.name" @input="hideNameMissingError">
                <label for="description">Description</label>
                <textarea class="input" id="description" name="description" rows="10"
                          v-model="newImage.description"></textarea>
              </div>
              <div class="upload-image-detail-half">
                <label for="publish">Publish image</label>
                <div class="publish-image-input-container">
                  <input id="publish" name="publish" type="checkbox" v-model="newImage.publish">
                  <p v-if="!newImage.publish" class="upload-image-publish-explanation">This image will only be
                    visible <strong>to you</strong>.</p>
                  <p v-else class="upload-image-publish-explanation">This image will be visible <strong>to
                    everyone</strong>.</p>
                </div>
                <div class="flex-row image-upload-buttons">
                  <button class="button-primary" @click="uploadImage">Upload</button>
                  <button class="button-secondary" @click="$router.back()">
                    <span>
                      Cancel
                    </span>
                  </button>
                </div>
                <div class="error-container">
                  <p v-if="showMissingNameError" class="input-error">Enter a name for the image.</p>
                  <p v-if="showMissingImageError" class="input-error">Select an image to upload.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import apiService from '@/services/apiService';
import {onBeforeUnmount, reactive, ref} from 'vue'
import {useRouter} from 'vue-router';

const {id} = defineProps({
  id: String,
})

const router = useRouter();

const fileInput = ref(null);
const file = ref([]);
const showUploadIcon = ref(true);
const showLoader = ref(false);
const previewUrl = ref(null);

const showMissingNameError = ref(false);
const showMissingImageError = ref(false);

const newImage = reactive({
  image_id: '',
  name: '',
  description: '',
  publish: false,
  album_id: id,
  extension: '',
  path: '',
  content_type: ''
})

function openFileDialog() {
  fileInput.value.click()
}

function onFileChange(event) {
  showMissingImageError.value = false;
  const selectedFile = event.target.files[0];
  if (!selectedFile) return;

  file.value = selectedFile;
  newImage.content_type = file.value.type;
  previewUrl.value = URL.createObjectURL(selectedFile);
  showUploadIcon.value = false;
}

async function uploadImage() {
  if (file.value.length === 0) {
    showMissingImageError.value = true;
    return;
  }
  if (newImage.name.trim() === "") {
    showMissingNameError.value = true;
    return;

  }

  showLoader.value = true;
  newImage.extension = getFileExtension(file.value.name);
  const response = await apiService.getUploadUrl(newImage);
  newImage.image_id = response.image_id;
  newImage.path = response.path;

  await apiService.uploadImageToStorage(file.value, response.url)
  await apiService.createImage(newImage)

  await router.push(`/albums/${id}`)
}

function getFileExtension(filename) {
  return filename.split('.').pop()
}

function hideNameMissingError() {
  showMissingNameError.value = false;
}

onBeforeUnmount(() => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
  }
})
</script>

<style scoped>
.upload-image-wrapper {
  min-height: 100vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  color: #323232;
}

.upload-image-container {
  display: flex;
  height: 100%;
  width: 100%;
  align-items: start;
  justify-content: start;
}

.upload-image-content {
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  height: 100%;
}

#uploadIcon {
  margin: 4rem 0;
}

.file-select-zone {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  width: 40%;
  height: 40%;
  border: 2px dashed lightgray;
  border-radius: 10px;
}

.image-preview {
  max-height: 100%;
  max-width: 100%;
}

.upload-image-detail {
  display: flex;
  align-items: start;
  justify-content: space-around;
  width: 70%;
  margin-top: 2rem;
}

.upload-image-detail-half {
  display: flex;
  flex-direction: column;
  width: 40%;
}

.publish-image-input-container {
  display: flex;
  align-items: center;
  justify-content: start;
  gap: 0.7rem;
  margin-bottom: 2rem;
}

.image-upload-buttons {
  gap: 10px
}

.upload-image-publish-explanation {
  font-size: 0.8rem;
  font-weight: lighter;
}

.loader-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  margin: 2rem 0 2rem 0;
}

.loader {
  width: 17px;
  aspect-ratio: 1;
  border-radius: 50%;
  background: #000;
  display: grid;
  animation: l22-0 2s infinite linear;
}

.loader:before,
.loader:after {
  content: "";
  grid-area: 1/1;
  margin: 15%;
  border-radius: 50%;
  background: inherit;
  transform: rotate(0deg) translate(150%);
  animation: l22 1s infinite;
}

.loader:after {
  animation-delay: -.5s
}

@keyframes l22-0 {
  100% {
    transform: rotate(1turn)
  }
}

@keyframes l22 {
  100% {
    transform: rotate(1turn) translate(150%)
  }
}
</style>